import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /**
   * Cache of section HTML keyed by request URL.
   * @type {Map<string, string>}
   */
  #sectionCache = new Map();

  /**
   * Cache of product JSON keyed by product handle.
   * @type {Map<string, object>}
   */
  #productCache = new Map();

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    // Preload product JSON so the first variant click is instant.
    this.#preloadProduct();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Preloads the product JSON into cache so the first variant click is instant.
   */
  #preloadProduct() {
    const handle = this.dataset.productHandle;
    if (!handle) return;

    if (this.#productCache.has(handle)) return;

    fetch(`/products/${handle}.js`)
      .then((r) => (r.ok ? r.json() : null))
      .then((product) => {
        if (product) this.#productCache.set(handle, product);
      })
      .catch(() => {});
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;

    // ----------------------------------------------------------------
    // 1. Instantly update the DOM price from data we already have.
    //    This is synchronous and takes ~1ms.
    // ----------------------------------------------------------------
    const variantFromDom = this.#getVariantFromDom(variantId);
    if (variantFromDom) {
      this.#applyVariantInstantly(variantFromDom);
    }

    // Dispatch native event for any external listeners (theme.liquid).
    document.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: {
          variant: variantFromDom,
          variantId,
          optionValueId,
          connectedProductUrl,
          productId: this.dataset.productId ?? '',
        },
      })
    );

    // ----------------------------------------------------------------
    // 2. If the variant object wasn't on the DOM, fetch it from the
    //    product JSON endpoint (CDN-cached, very fast). This runs in
    //    parallel and re-applies the price as soon as it lands.
    // ----------------------------------------------------------------
    if (!variantFromDom && variantId) {
      this.#lookupVariant(variantId).then((variant) => {
        if (variant) {
          this.#applyVariantInstantly(variant);
          document.dispatchEvent(
            new CustomEvent('variant:change', {
              bubbles: true,
              detail: {
                variant,
                variantId,
                optionValueId,
                connectedProductUrl,
                productId: this.dataset.productId ?? '',
              },
            })
          );
        }
      });
    }

    // ----------------------------------------------------------------
    // 3. Only fetch the section HTML when truly needed (combined
    //    listings, featured product section, quick-add).
    // ----------------------------------------------------------------
    const needsServerRender = loadsNewProduct || isOnFeaturedProductSection || !!morphElementSelector;

    if (needsServerRender) {
      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
        morphElementSelector,
        detail: { optionValueId, variantId, connectedProductUrl },
      });
    }

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Look up a variant by ID. Tries the DOM first, then the cached product
   * JSON, then fetches from `/products/{handle}.js` as a last resort.
   *
   * @param {string} variantId
   * @returns {Promise<object | null>}
   */
  async #lookupVariant(variantId) {
    if (!variantId) return null;

    const fromDom = this.#getVariantFromDom(variantId);
    if (fromDom) return fromDom;

    const handle = this.dataset.productHandle;
    if (!handle) return null;

    // Try cache
    if (this.#productCache.has(handle)) {
      const product = this.#productCache.get(handle);
      return this.#findVariantInProduct(product, variantId);
    }

    // Fetch
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      const product = await response.json();
      this.#productCache.set(handle, product);
      return this.#findVariantInProduct(product, variantId);
    } catch {
      return null;
    }
  }

  /**
   * @param {object} product
   * @param {string} variantId
   * @returns {object | null}
   */
  #findVariantInProduct(product, variantId) {
    const variants = product?.variants;
    if (!Array.isArray(variants)) return null;
    return variants.find((v) => String(v.id) === String(variantId)) ?? null;
  }

  /**
   * Best-effort lookup of the selected variant from JSON embedded in the page.
   *
   * @param {string} variantId
   * @returns {object | null}
   */
  #getVariantFromDom(variantId) {
    if (!variantId) return null;

    const jsonScript =
      document.querySelector('script[data-product-json]') ||
      document.querySelector('variant-picker script[type="application/json"]') ||
      document.querySelector('product-info script[type="application/json"]');

    if (jsonScript?.textContent) {
      try {
        const parsed = JSON.parse(jsonScript.textContent);
        const variants = parsed?.variants ?? parsed?.product?.variants;
        if (Array.isArray(variants)) {
          const match = variants.find((v) => String(v.id) === String(variantId));
          if (match) return match;
        }
      } catch {
        // ignore
      }
    }

    if (window.product?.variants) {
      const match = window.product.variants.find((v) => String(v.id) === String(variantId));
      if (match) return match;
    }

    return null;
  }

  /**
   * Applies the variant to the DOM instantly.
   *
   * @param {object} variant - The variant object from the product JSON.
   */
  #applyVariantInstantly(variant) {
    const moneyFormat =
      this.dataset.moneyFormat ||
      (window.theme && window.theme.moneyFormat) ||
      (window.Shopify && window.Shopify.money_format) ||
      '${{amount}}';

    const formatMoney = (cents) => {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        try {
          return window.Shopify.formatMoney(cents, moneyFormat);
        } catch {
          // fall through to manual formatting
        }
      }
      return '$' + (cents / 100).toFixed(2);
    };

    // Update every price block on the page
    document.querySelectorAll('.price, .price__container, [data-price], product-price').forEach((el) => {
      const regularEl = el.querySelector('.price-item--regular');
      const saleEl = el.querySelector('.price-item--sale');
      const compareEl = el.querySelector('.price-item--compare, .price__sale .price-item--regular');

      const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (hasDiscount) {
        if (saleEl) {
          saleEl.textContent = formatMoney(variant.price);
          saleEl.classList.remove('hidden');
        }
        if (regularEl) {
          regularEl.textContent = formatMoney(variant.compare_at_price);
          regularEl.classList.remove('hidden');
        }
        if (compareEl) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.classList.remove('hidden');
        }
      } else {
        if (regularEl) {
          regularEl.textContent = formatMoney(variant.price);
          regularEl.classList.remove('hidden');
        } else if (saleEl) {
          saleEl.textContent = formatMoney(variant.price);
          saleEl.classList.remove('hidden');
        }
        if (compareEl) compareEl.classList.add('hidden');
      }

      // If the theme uses a different structure, fall back to replacing the
      // first price-looking element's text.
      if (!regularEl && !saleEl) {
        const anyPrice = el.querySelector('[class*="price"]');
        if (anyPrice) anyPrice.textContent = formatMoney(variant.price);
      }
    });

    // Unit price
    const unitPriceEl = document.querySelector('[data-unit-price]');
    if (unitPriceEl) {
      if (variant.unit_price) {
        unitPriceEl.textContent = formatMoney(variant.unit_price);
        unitPriceEl.classList.remove('hidden');
      } else {
        unitPriceEl.classList.add('hidden');
      }
    }

    // Hidden variant ID inputs
    document.querySelectorAll('input[name="id"]').forEach((input) => {
      input.value = variant.id;
    });

    // Update the variant picker element's own data attribute
    this.dataset.selectedVariantId = variant.id;
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    const cachedHtml = this.#sectionCache.get(requestUrl);
    if (cachedHtml) {
      this.#applySectionHtml(cachedHtml, {
        morphElementSelector,
        deferredEventPromise,
      });
      return;
    }

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        this.#sectionCache.set(requestUrl, responseText);
        this.#applySectionHtml(responseText, {
          morphElementSelector,
          deferredEventPromise,
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  #applySectionHtml(responseText, { morphElementSelector, deferredEventPromise }) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

    const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
    const textContent = variantPickerJsonScript?.textContent;

    if (!textContent) {
      deferredEventPromise.resolve({
        variant: null,
        detail: {
          html,
          productId: this.dataset.productId ?? '',
          sourceId: this.selectedOptionId,
          resource: null,
        },
      });
      return;
    }

    let newProduct;

    if (morphElementSelector === 'main') {
      this.updateMain(html);
    } else if (morphElementSelector) {
      this.updateElement(html, morphElementSelector);
    } else {
      const { overflowList } = this.refs;
      const wasSwatchesExpanded =
        overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

      newProduct = this.updateVariantPicker(html);

      if (wasSwatchesExpanded) {
        const overflowListAfterMorph = overflowList;
        if (overflowListAfterMorph instanceof OverflowList) {
          overflowListAfterMorph.showAll();
        }
      }
    }

    if (this.selectedOptionId) {
      const variantData = JSON.parse(textContent);

      if (variantData && typeof variantData === 'object') {
        const productViewAttr = variantPickerJsonScript
          ?.closest('[view-event-payload]')
          ?.getAttribute('view-event-payload')
          ?.trim();

        deferredEventPromise.resolve({
          variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: variantData,
          },
        });

        return;
      }
    }

    deferredEventPromise.resolve({
      variant: null,
      detail: {
        html,
        productId: this.dataset.productId ?? '',
        newProduct,
        sourceId: this.selectedOptionId,
        resource: null,
      },
    });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}